import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Rating } from "primereact/rating";
import Paragraph from "../../components/common/Typography/Paragraph";
import Heading from "../../components/common/Typography/Heading";
import AuxiliaryText from "../../components/common/Typography/AuxiliaryText";
import SecondaryHeading from "../../components/common/Typography/SecondaryHeading";
import SpecialText from "../../components/common/Typography/SpecialText";
import MainButton from "../../components/common/MainButton/MainButton";
import useClickOutside from "../../Hooks/useClickOutside";
import Pro from "../../components/common/Tags/Pro";
import ProfileSpecialtyChips from "../../components/common/Profile/ProfileSpecialtyChips";
import {
  PUBLIC_POST_DETAIL_PROFILE_AVATAR_CLASS,
  PUBLIC_POST_DETAIL_PROFILE_AVATAR_WRAPPER_CLASS,
  PUBLIC_POST_DETAIL_PROFILE_PRO_BADGE_CLASS,
} from "../Public/shared/publicLayoutSpec";

const KeywordsPopup = ({ isOpen, onClose, userData }) => {
  const [dotMenuOpen, setDotMenuOpen] = useState(false);
  const dotMenuRef = useRef(null);
  
  useClickOutside(dotMenuRef, () => {
    setDotMenuOpen(false);
  });

  const keywordsData = [
    {
      id: 1,
      heading: "A",
      keywordsList: [
        "AC Installation",
        "AC Repair",
        "AC Maintenance",
        "Heating Systems",
        "Duct Cleaning",
        "Thermostat Installation",
        "Heat Pump Installation",
        "Air Filter Replacement",
        "Refrigerant Recharge",
        "Emergency AC Repair",
        "Architectural Design",
        "Building Plans",
        "Interior Design",
        "Renovation Plans",
        "3D Modeling",
        "Site Planning",
        "Sustainable Design",
        "Commercial Architecture",
        "Residential Design",
        "Project Management"
      ]
    },
    {
      id: 2,
      heading: "B",
      keywordsList: [
        "Brick Laying",
        "Block Work",
        "Retaining Walls",
        "Brick Repairs",
        "Chimney Building",
        "Brick Cleaning",
        "Mortar Repairs",
        "Brick Pointing",
        "Foundation Work",
        "Brick Paving",
        "Awning Installation",
        "Awning Repairs",
        "Retractable Awnings",
        "Patio Covers",
        "Shade Structures",
        "Commercial Awnings",
        "Residential Awnings",
        "Awning Maintenance",
        "Custom Awnings",
        "Awning Cleaning"
      ]
    },
    {
      id: 3,
      heading: "C",
      keywordsList: [
        "Custom Cabinets",
        "Kitchen Cabinets",
        "Bathroom Cabinets",
        "Built-in Wardrobes",
        "Cabinet Repairs",
        "Cabinet Refinishing",
        "Cabinet Installation",
        "Custom Joinery",
        "Cabinet Hardware",
        "Cabinet Design",
        "Custom Carpentry",
        "Door Installation",
        "Window Frames",
        "Deck Building",
        "Fence Construction",
        "Custom Furniture",
        "Wood Repairs",
        "Trim Work",
        "Staircase Building",
        "Wood Finishing",
        "Bathroom Caulking",
        "Kitchen Caulking",
        "Window Caulking",
        "Door Caulking",
        "Silicone Sealing",
        "Caulk Repairs",
        "Waterproof Caulking",
        "Exterior Caulking",
        "Caulk Removal",
        "Caulk Application",
        "Wall Cladding",
        "Vinyl Cladding",
        "Timber Cladding",
        "Metal Cladding",
        "Panel Installation",
        "Cladding Repairs",
        "Exterior Cladding",
        "Interior Panels",
        "Cladding Maintenance",
        "Custom Panels",
        "House Cleaning",
        "Commercial Cleaning",
        "Carpet Cleaning",
        "Window Cleaning",
        "Pressure Washing",
        "End of Lease Cleaning",
        "Spring Cleaning",
        "Office Cleaning",
        "Kitchen Cleaning",
        "Bathroom Cleaning",
        "Concrete Pouring",
        "Concrete Repairs",
        "Concrete Polishing",
        "Concrete Sealing",
        "Driveway Concreting",
        "Pathway Concreting",
        "Concrete Cutting",
        "Concrete Staining",
        "Concrete Resurfacing",
        "Concrete Foundations"
      ]
    },
    {
      id: 4,
      heading: "D",
      keywordsList: [
        "Building Demolition",
        "Site Excavation",
        "Trenching",
        "Foundation Excavation",
        "Debris Removal",
        "Asbestos Removal",
        "Tree Removal",
        "Site Clearing",
        "Demolition Planning",
        "Excavation Equipment"
      ]
    },
    {
      id: 5,
      heading: "E",
      keywordsList: [
        "Electrical Installation",
        "Electrical Repairs",
        "Lighting Installation",
        "Power Point Installation",
        "Switchboard Upgrades",
        "Electrical Safety Inspections",
        "Emergency Electrical",
        "LED Lighting",
        "Electrical Testing",
        "Smart Home Wiring"
      ]
    },
    {
      id: 6,
      heading: "F",
      keywordsList: [
        "Fence Installation",
        "Gate Installation",
        "Fence Repairs",
        "Pool Fencing",
        "Security Fencing",
        "Timber Fencing",
        "Metal Fencing",
        "Automatic Gates",
        "Fence Painting",
        "Fence Maintenance",
        "Hardwood Flooring",
        "Laminate Flooring",
        "Vinyl Flooring",
        "Tile Installation",
        "Carpet Installation",
        "Floor Repairs",
        "Floor Sanding",
        "Floor Polishing",
        "Underfloor Heating",
        "Floor Leveling"
      ]
    },
    {
      id: 7,
      heading: "G",
      keywordsList: [
        "Garage Door Installation",
        "Garage Door Repairs",
        "Automatic Garage Doors",
        "Garage Door Springs",
        "Garage Door Motors",
        "Garage Door Maintenance",
        "Garage Door Sensors",
        "Roller Doors",
        "Sectional Doors",
        "Garage Door Security",
        "Window Installation",
        "Glass Replacement",
        "Mirror Installation",
        "Shower Screens",
        "Glass Repairs",
        "Double Glazing",
        "Safety Glass",
        "Glass Cutting",
        "Glass Polishing",
        "Custom Glass"
      ]
    },
    {
      id: 8,
      heading: "H",
      keywordsList: [
        "General Repairs",
        "Home Maintenance",
        "Small Jobs",
        "Assembly Services",
        "Hanging Pictures",
        "Furniture Assembly",
        "Minor Repairs",
        "Home Improvements",
        "Odd Jobs",
        "Emergency Repairs"
      ]
    },
    {
      id: 9,
      heading: "I",
      keywordsList: [
        "Ceiling Insulation",
        "Wall Insulation",
        "Underfloor Insulation",
        "Bulk Insulation",
        "Reflective Insulation",
        "Insulation Removal",
        "Sound Insulation",
        "Thermal Insulation",
        "Insulation Upgrades",
        "Energy Efficiency"
      ]
    },
    {
      id: 10,
      heading: "L",
      keywordsList: [
        "Garden Design",
        "Lawn Mowing",
        "Garden Maintenance",
        "Tree Planting",
        "Garden Irrigation",
        "Paving Installation",
        "Garden Lighting",
        "Retaining Walls",
        "Garden Cleanup",
        "Landscape Design"
      ]
    },
    {
      id: 11,
      heading: "M",
      keywordsList: [
        "Mould Removal",
        "Asbestos Removal",
        "Mould Testing",
        "Asbestos Testing",
        "Mould Prevention",
        "Asbestos Encapsulation",
        "Mould Remediation",
        "Asbestos Disposal",
        "Mould Inspection",
        "Asbestos Surveys"
      ]
    },
    {
      id: 12,
      heading: "P",
      keywordsList: [
        "Interior Painting",
        "Exterior Painting",
        "House Painting",
        "Commercial Painting",
        "Paint Repairs",
        "Color Consultation",
        "Wallpaper Installation",
        "Paint Stripping",
        "Spray Painting",
        "Paint Touch-ups",
        "Termite Control",
        "Rodent Control",
        "Spider Control",
        "Ant Control",
        "Cockroach Control",
        "Pest Inspection",
        "Pest Prevention",
        "Bed Bug Treatment",
        "Pest Management",
        "Emergency Pest Control",
        "Wall Plastering",
        "Ceiling Plastering",
        "Plaster Repairs",
        "Cornice Installation",
        "Plasterboard Installation",
        "Plaster Finishing",
        "Plaster Restoration",
        "Plaster Painting",
        "Plaster Molding",
        "Plaster Texturing",
        "Pipe Installation",
        "Leak Repairs",
        "Drain Cleaning",
        "Hot Water Systems",
        "Toilet Repairs",
        "Tap Installation",
        "Shower Repairs",
        "Emergency Plumbing",
        "Pipe Relining",
        "Plumbing Maintenance",
        "Pool Installation",
        "Pool Maintenance",
        "Pool Repairs",
        "Spa Installation",
        "Pool Cleaning",
        "Pool Equipment",
        "Pool Heating",
        "Pool Fencing",
        "Pool Safety",
        "Pool Renovation"
      ]
    },
    {
      id: 13,
      heading: "R",
      keywordsList: [
        "House Rendering",
        "Wall Rendering",
        "Render Repairs",
        "Acrylic Rendering",
        "Cement Rendering",
        "Texture Rendering",
        "Render Painting",
        "Render Cleaning",
        "Render Sealing",
        "Custom Rendering",
        "Roof Installation",
        "Roof Repairs",
        "Roof Maintenance",
        "Gutter Installation",
        "Gutter Cleaning",
        "Roof Painting",
        "Roof Inspection",
        "Emergency Roofing",
        "Roof Restoration",
        "Roof Ventilation"
      ]
    },
    {
      id: 14,
      heading: "S",
      keywordsList: [
        "Security Systems",
        "CCTV Installation",
        "Alarm Systems",
        "Smart Home Automation",
        "Access Control",
        "Security Cameras",
        "Home Automation",
        "Security Monitoring",
        "Smart Locks",
        "Security Maintenance",
        "Solar Panel Installation",
        "Solar System Maintenance",
        "Solar Repairs",
        "Solar Cleaning",
        "Solar Inverters",
        "Solar Batteries",
        "Solar Monitoring",
        "Solar Upgrades",
        "Solar Inspections",
        "Solar Financing",
        "Custom Staircases",
        "Staircase Repairs",
        "Staircase Renovation",
        "Staircase Design",
        "Staircase Installation",
        "Staircase Refinishing",
        "Staircase Safety",
        "Staircase Railings",
        "Staircase Maintenance",
        "Staircase Restoration",
        "Stone Masonry",
        "Stone Repairs",
        "Stone Installation",
        "Stone Cleaning",
        "Stone Restoration",
        "Stone Sealing",
        "Stone Polishing",
        "Custom Stonework",
        "Stone Maintenance",
        "Stone Carving",
        "Foundation Repairs",
        "Structural Inspections",
        "Crack Repairs",
        "Structural Reinforcement",
        "Building Repairs",
        "Structural Assessment",
        "Foundation Stabilization",
        "Structural Maintenance",
        "Emergency Repairs",
        "Structural Engineering"
      ]
    },
    {
      id: 15,
      heading: "T",
      keywordsList: [
        "Floor Tiling",
        "Wall Tiling",
        "Bathroom Tiling",
        "Kitchen Tiling",
        "Tile Repairs",
        "Tile Grouting",
        "Tile Sealing",
        "Tile Cleaning",
        "Custom Tiling",
        "Tile Restoration"
      ]
    },
    {
      id: 16,
      heading: "W",
      keywordsList: [
        "Bathroom Waterproofing",
        "Basement Waterproofing",
        "Roof Waterproofing",
        "Waterproofing Repairs",
        "Waterproofing Inspection",
        "Waterproofing Maintenance",
        "Liquid Waterproofing",
        "Membrane Waterproofing",
        "Waterproofing Sealing",
        "Waterproofing Testing",
        "Blind Installation",
        "Curtain Installation",
        "Shade Installation",
        "Blind Repairs",
        "Curtain Repairs",
        "Custom Blinds",
        "Motorized Blinds",
        "Blind Cleaning",
        "Curtain Cleaning",
        "Window Treatments"
      ]
    }
  ];

  const handleItemSelect = (id) => {
    const element = document.getElementById(`keyword-${id}`);
    if (element) {
      element.scrollIntoView({ block: "start", behavior: "smooth" });
      setTimeout(() => {
        const offsetMargin = 40;
        const scrollPosition = window.scrollY;
        const elementRect = element.getBoundingClientRect();
        const elementTop = elementRect.top + scrollPosition;
        window.scrollTo({
          top: elementTop - offsetMargin,
          behavior: "smooth",
        });
      }, 300);
    }
  };

  const dropIn = {
    visible: {
      y: "0",
      opacity: 1,
      transition: {
        duration: 0.9,
        type: "ease",
        damping: 25,
        stiffness: 500,
      },
    },
    exit: {
      y: "100vh",
      opacity: 0,
      transition: {
        duration: 0.9,
        type: "ease",
        damping: 25,
        stiffness: 500,
      },
    },
  };

  if (!isOpen) return null;

  return (
    <motion.div
      variants={dropIn}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="fixed top-0 left-0 w-screen h-screen flex flex-col justify-center items-center z-50 bg-pastalBlueWhite hidden-scrollbar"
    >
      <div className="fixed w-screen h-screen top-0 z-40 hidden-scrollbar overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-end mx-[5%] xl:mx-auto xl:w-[800px] 2xl:w-[800px] h-[60px] sm:h-[80px] sticky top-0 z-10">
          <button type="button" onClick={onClose} aria-label="Close">
            <i className="fi fi-rs-cross text-base text-offblack cursor-pointer"></i>
          </button>
        </div>

        {/* Content */}
        <div className="pt-20 sm:pt-24 pb-20">
          {/* Centered profile section */}
          {userData && (
            <div className="mx-[5%] xl:mx-auto xl:w-[800px] 2xl:w-[800px]">
              <div className="mb-10 w-full">
                <div className="flex w-full justify-center pb-2.5">
                  <Link
                    to={`/poster-/?id=${userData?.id}`}
                    className="inline-flex"
                  >
                    <div className={PUBLIC_POST_DETAIL_PROFILE_AVATAR_WRAPPER_CLASS}>
                      <img
                        src={userData?.profilePicture || require('../../assets/img/default-image.jpg')}
                        alt={userData?.businessName}
                        className={PUBLIC_POST_DETAIL_PROFILE_AVATAR_CLASS}
                      />
                      {userData?.userPlan?.plan?.name?.toLowerCase() === 'pro' && (
                        <span className={PUBLIC_POST_DETAIL_PROFILE_PRO_BADGE_CLASS}>
                          <Pro className="bg-primaryblue text-white" />
                        </span>
                      )}
                    </div>
                  </Link>
                </div>
                <div className="mt-2 flex flex-col items-center gap-2">
                  <SpecialText variant="ProfileName">
                    {userData?.businessName}
                  </SpecialText>
                  <SpecialText variant="ProfileMeta">
                    {userData?.serviceCategory?.name || 'Category'}
                  </SpecialText>
                  <ProfileSpecialtyChips />
                  <SpecialText variant="ProfileMicro">
                    {userData?.location || ''}
                  </SpecialText>
                  <SpecialText variant="Date">
                    Member since {userData?.startDate ? new Date(userData.startDate).getFullYear() : 'N/A'}
                  </SpecialText>
                  {/* Profile average rating: same as service detail page */}
                  <div className="flex items-center justify-center">
                    <div className="flex items-center">
                      <Rating
                        value={4.3}
                        stars={1}
                        style={{ gap: 5 }}
                        cancel={false}
                        readOnly
                      />
                      <SpecialText variant="Counter" className="ml-1 text-offblack">
                        4.3
                      </SpecialText>
                    </div>
                  </div>
                  {/* <div className="flex items-center gap-3 mt-2">
                <MainButton
                  variant="small"
                  className=""
                  onClick={() => {
                    // Handle get in touch functionality
                  }}
                >
                  Get in Touch
                </MainButton>
                <div className="relative">
                  <MainButton
                    variant="outlinesmall"
                    className="!w-auto aspect-square"
                    onClick={() => setDotMenuOpen(!dotMenuOpen)}
                  >
                    <i className="fi fi-bs-menu-dots text-base flex items-center justify-center"></i>
                  </MainButton>
                  {dotMenuOpen && (
                    <div 
                      ref={dotMenuRef}
                      className="flex flex-col rounded-xl w-48 py-3 absolute z-30 bg-white text-offblack mt-2 left-1/2 transform -translate-x-1/2 shadow-lg border border-gray-200"
                    >
                      <button className="block px-4 py-2 hover:text-primaryblue w-full text-center">
                        <AuxiliaryText variant={"MenuDropdown"}>
                          Follow
                        </AuxiliaryText>
                      </button>
                      <button className="block px-4 py-2 hover:text-primaryblue w-full text-center">
                        <AuxiliaryText variant={"MenuDropdown"}>
                          Call
                        </AuxiliaryText>
                      </button>
                      <button 
                        className="block px-4 py-2 hover:text-primaryblue w-full text-center"
                      >
                        <AuxiliaryText variant={"MenuDropdown"}>
                          Share
                        </AuxiliaryText>
                      </button>
                      <button 
                        className="block px-4 py-2 hover:text-primaryblue w-full text-center"
                      >
                        <AuxiliaryText variant={"MenuDropdown"}>
                          Report
                        </AuxiliaryText>
                      </button>
                      <button className="block px-4 py-2 hover:text-primaryblue w-full text-center">
                        <AuxiliaryText variant={"MenuDropdown"}>
                          Block
                        </AuxiliaryText>
                      </button>
                    </div>
                  )}
                </div>
              </div> */}
                </div>
              </div>
            </div>
          )}

          {/* Title */}
          {/* <div className="text-center mt-8 mb-12">
            <Heading variant={"MainHeading"} className="text-offblack mb-4">
              All Keywords
            </Heading>
            <Paragraph variant={"MainParagraph"} className="text-offblack">
              Browse through our comprehensive list of trade categories and services
            </Paragraph>
          </div> */}

          {/* A-Z Navigation - Exact same as AtoZFilters */}
          {/* <div className="grid grid-cols-12 gap-1 mx-[5%] xl:mx-auto xl:w-[800px] 2xl:w-[800px] mt-10 2xl:mt-16">
            {keywordsData.map((e, index) => {
              return (
                <>
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleItemSelect(e.id)}
                  >
                    <div
                      className={
                        "col-span-1 bg-white text-offblack flex items-center justify-center cursor-pointer hover:text-primaryblue py-1"
                      }
                      key={e.id}
                    >
                      <AuxiliaryText variant={"FieldLabel"}>
                        {e.heading}
                      </AuxiliaryText>
                    </div>
                  </button>
                </>
              );
            })}
          </div> */}

          {/* Keywords List - Exact same structure as AtoZFilters */}
          {keywordsData.map((e, index) => {
            return (
              <>
                <div
                  key={index}
                  id={`keyword-${e.id}`}
                  className="mx-[5%] xl:mx-auto xl:w-[800px] 2xl:w-[800px] pb-2 border-b border-b-mediumgray mt-7 sm:mt-10 lg:mt-20"
                >
                  <SecondaryHeading variant={"MainParagraphHeading"} className={"text-offblack"}>
                    {e.heading}
                  </SecondaryHeading>
                </div>
                {e.keywordsList ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 mx-[5%] xl:mx-auto xl:w-[800px] 2xl:w-[800px] mt-5">
                    {e.keywordsList.map((keyword, index) => {
                      return (
                        <>
                          <div key={index} className="col-span-1">
                            <div className="text-offblack hover:text-primaryblue">
                              <Link to="/category/plumbing">
                                <Paragraph
                                  variant={"BodyCompact"}
                                  className={"mb-2 text-offblack hover:text-primaryblue cursor-pointer"}
                                >
                                  {keyword}
                                </Paragraph>
                              </Link>
                            </div>
                          </div>
                        </>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <Paragraph
                      variant={"MainParagraph"}
                      className={"text-center text-offblack mt-5"}
                    >
                      Sorry, this category keywords aren't available.
                    </Paragraph>
                  </>
                )}
              </>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export default KeywordsPopup; 